/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";

const OurMember = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupedMembers, setGroupedMembers] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [uiMembershipMap, setUiMembershipMap] = useState({});

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const PAID_MEMBERS_API_URL = `${API_BASE}/paid-members`;
  const OUR_MEMBERS_API_URL = `${API_BASE}/UIourMembers`;
  const UI_MEMBERSHIP_API_URL = `${API_BASE}/UImemberships`;
  const API_MEDIA = `${API_BASE}/UImedia`;

  // Fetch image securely with auth
  const fetchImageWithAuth = async (photoId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_MEDIA}/${photoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      console.error("Error fetching member photo:", err);
      return "/images/ourMembers-imgs/unknown.webp"; // Fallback image
    }
  };

  // Fetch UI Memberships for sequencing
  const fetchUiMemberships = async () => {
    try {
      const res = await axios.get(UI_MEMBERSHIP_API_URL);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];

      const map = {};
      data.forEach((m) => {
        map[m.title] = {
          seq: m.seqNumber,
          title: m.title,
        };
      });

      setUiMembershipMap(map);
      return map;
    } catch (err) {
      console.error("Error fetching UI memberships:", err);
      return {};
    }
  };

  // Fetch paid members from API
  const fetchPaidMembers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(PAID_MEMBERS_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Extract members from the nested structure
      let membersData = [];
      
      if (res.data && Array.isArray(res.data.items)) {
        membersData = res.data.items;
      } else if (Array.isArray(res.data)) {
        membersData = res.data;
      } else if (res.data && Array.isArray(res.data.results)) {
        membersData = res.data.results;
      } else if (res.data && typeof res.data === 'object') {
        membersData = [res.data];
      } else {
        console.warn("Unexpected Paid Members API response format:", res.data);
        membersData = [];
      }

      // Fetch images for each paid member
      const paidMembersWithImages = await Promise.all(
        membersData
          .filter(member => 
            member.memberStatus === "active" &&
            member.paymentStatus === "success" &&
            member.verificationStatus === "verified"
          )
          .map(async (member) => {
            let photoUrl = "/images/ourMembers-imgs/unknown.webp";
            
            if (member.formData?.profilePhoto?.id) {
              try {
                photoUrl = await fetchImageWithAuth(member.formData.profilePhoto.id);
              } catch (error) {
                console.error(`Failed to load photo for ${member.formData?.fullName}:`, error);
              }
            }

            // Format date for display
            const formattedDate = member.formData?.dateJoined 
              ? new Date(member.formData.dateJoined).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })
              : "Unknown date";

            return {
              id: member.id || member._id,
              name: member.formData?.fullName || "Unknown Member",
              photo: photoUrl,
              dateJoined: formattedDate,
              bio: member.formData?.biography || "No bio available",
              planTitle: member.planTitle || "Community Member",
              memberType: member.formData?.memberType || "Community Member",
              contactNumber: member.formData?.contactNumber || "Not provided",
              email: member.formData?.email || "Not provided",
              isActive: true,
              matchKey: member.planTitle || member.formData?.memberType || "Community Member",
              source: "paid"
            };
          })
      );

      return paidMembersWithImages;
    } catch (err) {
      console.error("Error fetching paid members:", err);
      return [];
    }
  };

  // Fetch our members from API
  const fetchOurMembers = async () => {
    try {
      const res = await axios.get(OUR_MEMBERS_API_URL);
      
      let membersData = [];
      
      if (Array.isArray(res.data)) {
        membersData = res.data;
      } else if (res.data && Array.isArray(res.data.results)) {
        membersData = res.data.results;
      } else if (res.data && Array.isArray(res.data.items)) {
        membersData = res.data.items;
      } else if (res.data && typeof res.data === 'object') {
        membersData = [res.data];
      } else {
        console.warn("Unexpected Our Members API response format:", res.data);
        membersData = [];
      }

      // Fetch images for each our member
      const ourMembersWithImages = await Promise.all(
        membersData
          .filter(member => member.isActive !== false)
          .map(async (member) => {
            let photoUrl = "/images/ourMembers-imgs/unknown.webp";
            
            if (member.photo?.id) {
              try {
                photoUrl = await fetchImageWithAuth(member.photo.id);
              } catch (error) {
                console.error(`Failed to load photo for ${member.name}:`, error);
              }
            }

            // Format date for display
            const formattedDate = member.dateJoined 
              ? new Date(member.dateJoined).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long'
                })
              : "Unknown date";

            return {
              id: member.id || member._id,
              name: member.name || "Unknown Member",
              photo: photoUrl,
              dateJoined: formattedDate,
              bio: member.bio || "No bio available",
              planTitle: member.memberType || "Community Member",
              memberType: member.memberType || "Community Member",
              contactNumber: member.contactNumber || "Not provided",
              email: member.email || "Not provided",
              isActive: member.isActive !== undefined ? member.isActive : true,
              joiningType: member.joiningType || "Not specified",
              matchKey: member.memberType || "Community Member",
              source: "our"
            };
          })
      );

      return ourMembersWithImages;
    } catch (err) {
      console.error("Error fetching our members:", err);
      return [];
    }
  };

  // Fetch all members from both APIs
  const fetchAllMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch UI memberships first for sequencing
      const uiMap = await fetchUiMemberships();
      
      // Fetch from both APIs simultaneously
      const [paidMembers, ourMembers] = await Promise.all([
        fetchPaidMembers(),
        fetchOurMembers()
      ]);

      // Combine both arrays and add sequencing
      const allMembers = [...paidMembers, ...ourMembers].map((member) => {
        const match = uiMap[member.matchKey];
        return {
          ...member,
          seq: match?.seq ?? 999,
          displayTitle: match?.title ?? member.matchKey,
        };
      });

      // Group members by displayTitle
      const grouped = allMembers.reduce((acc, member) => {
        const title = member.displayTitle;
        if (!acc[title]) {
          acc[title] = [];
        }
        acc[title].push(member);
        return acc;
      }, {});

      // Sort groups by sequence number
      const sortedGrouped = Object.entries(grouped)
        .sort((a, b) => a[1][0].seq - b[1][0].seq)
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});

      setGroupedMembers(sortedGrouped);
      setMembers(allMembers);
      
    } catch (err) {
      console.error("Error fetching all members:", err);
      setError("Failed to load members. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMembers();
  }, []);

  // Function to get badge color based on member type
  const getMemberTypeBadgeColor = (memberType) => {
    const colors = {
      'Community Member': 'bg-blue-100 text-blue-800',
      'Premium Member': 'bg-purple-100 text-purple-800',
      'Gold Member': 'bg-yellow-100 text-yellow-800',
      'Corporate Member': 'bg-indigo-100 text-indigo-800',
      'Platinum Member': 'bg-gray-100 text-gray-800',
      'Patron Member': 'bg-red-100 text-red-800',
      'Board Member': 'bg-green-100 text-green-800',
      'Executive Member': 'bg-teal-100 text-teal-800',
      'General Member': 'bg-orange-100 text-orange-800',
      'Volunteer': 'bg-pink-100 text-pink-800',
      'Advisor': 'bg-cyan-100 text-cyan-800'
    };
    return colors[memberType] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen px-4 py-12 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading members...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen px-4 py-12 bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-4xl mb-4">⚠️</div>
          <p className="text-white text-lg mb-4">{error}</p>
          <button
            onClick={fetchAllMembers}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-12 bg-gray-900">
      {/* Top Description */}
      <div className="max-w-4xl mx-auto text-center mb-16">
        <h1 className="text-4xl font-bold mb-6 text-white">
          Our Members
        </h1>
        <p className="text-lg text-white leading-relaxed">
          Meet the wonderful people who form our community. Together, we're building
          meaningful connections and making a difference.
        </p>
        
        {/* Statistics */}
        {/* <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-yellow-500">{members.length}</div>
            <div className="text-gray-300 text-sm">Total Members</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-yellow-500">
              {Object.keys(groupedMembers).length}
            </div>
            <div className="text-gray-300 text-sm">Member Categories</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-2xl font-bold text-yellow-500">
              {members.filter(m => m.source === "paid").length}
            </div>
            <div className="text-gray-300 text-sm">Paid Members</div>
          </div>
        </div> */}
      </div>

      {/* Members Grid Grouped by Plan Title */}
      {members.length === 0 ? (
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="text-gray-400 text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-white mb-2">No Active Members</h3>
          <p className="text-gray-400">There are currently no active verified members to display.</p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          {Object.entries(groupedMembers).map(([title, planMembers]) => (
            <div key={title} className="mb-16">
              {/* Plan Title Heading with Sequence Number */}
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">
                  {title}
                </h2>
                <div className="w-24 h-1 bg-yellow-500 mx-auto"></div>
                <p className="text-gray-400 mt-2">
                  {planMembers.length} {planMembers.length === 1 ? 'member' : 'members'}
                </p>
              </div>

              {/* Members Grid for this Plan */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 px-4">
                {planMembers.map((member) => (
                  <div 
                    key={`${member.source}-${member.id}`} 
                    className="bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-105 border border-gray-700"
                  >
                    <div className="p-6 text-center">
                      <div className="relative inline-block">
                        <img
                          src={member.photo}
                          alt={member.name}
                          className="w-32 h-32 object-cover rounded-full mx-auto mb-4 border-4 border-yellow-500 shadow-lg"
                          onError={(e) => {
                            e.target.src = "/images/ourMembers-imgs/unknown.webp";
                          }}
                        />
                        {/* Active Status Indicator */}
                        <div className="absolute bottom-2 right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        
                        {/* Source Badge */}
                        {/* <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                          member.source === "paid" 
                            ? "bg-green-500 text-white" 
                            : "bg-blue-500 text-white"
                        }`}>
                          {member.source === "paid" ? "Paid" : "Team"}
                        </div> */}
                      </div>
                      
                      {/* Member Name */}
                      <h2 className="text-xl font-bold text-white mb-2">{member.name}</h2>
                      
                      {/* Member Type Badge */}
                      {member.memberType && member.memberType !== title && (
                        <div className="mb-3">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getMemberTypeBadgeColor(member.memberType)}`}>
                            {member.memberType}
                          </span>
                        </div>
                      )}
                      
                      {/* Joining Type (for our members) */}
                      {/* {member.joiningType && member.joiningType !== "Not specified" && (
                        <div className="mb-3">
                          <span className="inline-block bg-purple-600 text-white px-2 py-1 rounded-full text-xs">
                            Joined via: {member.joiningType}
                          </span>
                        </div>
                      )} */}
                      
                      {/* Contact Information */}
                      {/* <div className="mb-4 space-y-2">
                        {member.contactNumber && member.contactNumber !== "Not provided" && (
                          // <div className="flex items-center justify-center text-sm text-gray-300">
                          //   <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          //     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          //   </svg>
                            {member.contactNumber}
                          // </div>
                        )}
                        {member.email && member.email !== "Not provided" && (
                          <div className="flex items-center justify-center text-sm text-gray-300">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {member.email}
                          </div>
                        )}
                      </div> */}
                      
                      {/* Biography */}
                      <p className="text-gray-300 text-sm mb-4 leading-relaxed line-clamp-3">
                        {member.bio}
                      </p>
                      
                      {/* Join Date */}
                      <div className="flex items-center justify-center text-sm text-gray-400">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Joined {member.dateJoined}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OurMember;